import type { DefaultOptionType } from 'rc-select/lib/Select';
import React from 'react';

import DateTime from '~/components/Columns/DateTime';
import { prefetchOptions, toLabel } from '~/components/Columns/helpers';

import { DateFilterPresets } from '~/types/components';
import {
  type CollectionColumnDefinition,
  type ColumnDefinition,
  type ItemColumnDefinition,
  ModelColumns,
} from '~/types/components';
import type { BounceReason, Contact, Email, PointageResult, Tag } from '~/types/models';
import { bounceReasonLabel, pointageResultLabel } from '~/types/models';

import { labelsToOptions } from '~/helpers';

const emailContactColumn: ItemColumnDefinition<Email, Contact> = {
  title: 'Contact',
  key: 'contact',
  sortable: false,
  type: 'item',
  render: (value) => {
    return (
      <span>{value?.firstName} {value?.lastName}</span>
    );
  },
};

const emailTagsColumn: CollectionColumnDefinition<Email, Tag> = {
  title: 'Étiquettes',
  key: 'tags',
  mapOptions(items: Tag[]): DefaultOptionType[] {
    return items.map((i) => ({
      value: i.id,
      key: i.id,
      label: i.name,
    }));
  },
  prefetchOptions: prefetchOptions<Tag>('tags'),
  sortable: false,
  toLabel: toLabel('tags', 'Étiquette'),
  type: 'collection',
  render: (value) => {
    if (!Array.isArray(value)) {
      return <em>Chargement...</em>;
    }

    if (value.length === 0) {
      return <em>Aucune étiquette</em>;
    }

    return (
      <ul style={{ margin: 0, padding: 0 }}>
        {value.map((g) => (
          <li key={g.id}>{g.name}</li>
        ))}
      </ul>
    );
  },
};

export const Columns: Array<ColumnDefinition<Email>> = [
  {
    key: 'bounced',
    options: labelsToOptions(bounceReasonLabel),
    readOnly: true,
    render: (value: BounceReason) => <span>{bounceReasonLabel[value] ?? 's. o.'}</span>,
    sortable: true,
    title: 'Refusé',
    type: 'enum',
  },
  {
    key: 'clicked',
    nullable: 'Jamais cliqué',
    notNullable: 'Cliqué',
    presets: DateFilterPresets.Past,
    readOnly: true,
    render: (v) => <DateTime value={v} />,
    sortable: true,
    title: 'Premier clic',
    type: 'datetime',
  },
  {
    key: 'lastResult',
    options: labelsToOptions(pointageResultLabel),
    readOnly: true,
    render: (value: PointageResult) => <span>{pointageResultLabel[value] ?? 's. o.'}</span>,
    sortable: true,
    title: 'Dernier pointage',
    type: 'enum',
  },
  {
    key: 'opened',
    nullable: 'Jamais ouvert',
    notNullable: 'Ouvert',
    presets: DateFilterPresets.Past,
    readOnly: true,
    render: (v) => <DateTime value={v} />,
    sortable: true,
    title: 'Ouverture',
    type: 'datetime',
  },
  {
    key: 'sent',
    nullable: 'Jamais envoyé',
    notNullable: 'Envoyé',
    presets: DateFilterPresets.Past,
    render: (v) => <DateTime value={v} />,
    sortable: true,
    title: 'Envoi',
    type: 'datetime',
  },
  {
    key: 'toEmail',
    readOnly: true,
    sortable: true,
    title: 'Courriel',
    type: 'string',
  },
  emailContactColumn,
  emailTagsColumn,
  ...ModelColumns,
];
